import { useAuthContext } from 'contexts/AuthContext';
import { useEffect, useState } from 'react';
import type { DropdownProps } from 'semantic-ui-react';
import { Dropdown, Menu, Popup, Segment } from 'semantic-ui-react';
import type { Option } from 'types';

import PatientOrders from '../food/orders';

/**
 * What the food-card view accepts. Two call sites: `components/home/index.tsx`
 * (roster-wide, `patientId={null}`) and `components/patient/index.tsx` (member).
 * Every field except `patientId` is forwarded verbatim to `PatientOrders`, so
 * those types come from that component.
 *
 * `patientId` is spelled out because this file branches on it to choose which of
 * the two `PatientOrders` renders to use.
 */
type PatientOrdersProps = React.ComponentProps<typeof PatientOrders>;

type FoodCardProps = {
  /** `null` selects the roster-wide mode. */
  patientId?: string | null;
} & {
  /*
   * Re-stated with an explicit `| undefined` rather than `Pick`ed through:
   * `Pick` preserves the plain-optional form, which under
   * `exactOptionalPropertyTypes` rejects the JSX attributes holding absent
   * values that the call sites pass. Value types still come from `PatientOrders`.
   */
  [K in
    | 'profileData'
    | 'patient'
    | 'org'
    | 'fetchOrders'
    | 'onPatientSelected'
    | 'allReferralData'
    | 'programBudget'
    | 'setProgramBudget']?: PatientOrdersProps[K] | undefined;
};

/**
 * Food Card ordering view — simplified food UI with food-card vendors and orders only.
 */
function FoodCard(props: FoodCardProps) {
  const authInfo = useAuthContext();
  const [selectedVendor, setSelectedVendor] = useState('All');
  const [selectedVendorName, setSelectedVendorName] = useState('All');
  const [selectedOrg, setSelectedOrg] = useState('All');
  const [vendorsOptions, setVendorsOptions] = useState<Option[]>([]);
  const [ordersMealEquivalents, setOrdersMealEquivalents] = useState(0);
  const [programOrdersCost, setProgramOrdersCost] = useState(0);

  const foodCardVendorIds = (authInfo.foodCardVendors ?? []).map((vendor: Option) => vendor.value);

  useEffect(() => {
    setVendorsOptions(authInfo.foodCardVendors ?? []);
  }, [authInfo.foodCardVendors]);

  const vendorDropdownOptions = [{ text: 'All', key: 'All', value: 'All' }].concat(vendorsOptions);

  // Single-select over the string-valued option lists built above, so `value`
  // arrives as a string despite `DropdownProps` admitting arrays and booleans.
  const handleVendorSelection = (e: React.SyntheticEvent<HTMLElement>, { value }: DropdownProps) => {
    setSelectedVendor(value as string);
    const match = vendorDropdownOptions.find((option) => option.value === value);
    setSelectedVendorName(match?.text ?? (value as string));
  };

  const handleOrgSelection = (e: React.SyntheticEvent<HTMLElement>, { value }: DropdownProps) => {
    setSelectedOrg(value as string);
  };

  return (
    <div>
      <Menu pointing>
        {!props.patientId && (
          <Popup
            flowing
            trigger={
              <Dropdown
                scrolling
                className="link item"
                style={{ zIndex: '99999' }}
                onChange={handleOrgSelection}
                options={[{ text: 'All', key: 'All', value: 'All' }].concat(authInfo.payers)}
                value={selectedOrg}
              />
            }
            content="Payer Organization"
            on="hover"
          />
        )}
        <Popup
          flowing
          trigger={
            <Dropdown
              scrolling
              className="link item"
              onChange={handleVendorSelection}
              options={vendorDropdownOptions}
              value={selectedVendor}
            />
          }
          content="Vendors"
          on="hover"
        />
        <Menu.Item key="orders" name="Orders" active>
          Orders
        </Menu.Item>
      </Menu>
      <Segment>
        {selectedVendor && selectedVendor.length > 0 && props.patientId !== null && (
          <PatientOrders
            profileData={props.profileData}
            patient={props.patient}
            patientId={props.patientId}
            vendor={selectedVendor}
            vendorName={selectedVendorName}
            selectedOrg={props.org}
            org={props.org}
            foodCardOrdersOnly
            fetchOrders={props.fetchOrders}
            foodCardVendorIds={foodCardVendorIds}
            ordersMealEquivalents={ordersMealEquivalents}
            setOrdersMealEquivalents={setOrdersMealEquivalents}
            programOrdersCost={programOrdersCost}
            setProgramOrdersCost={setProgramOrdersCost}
            allReferralData={props.allReferralData}
            programBudget={props.programBudget}
            setProgramBudget={props.setProgramBudget}
          />
        )}
        {selectedVendor && selectedVendor.length > 0 && props.patientId === null && (
          <PatientOrders
            profileData={props.profileData}
            selectedOrg={selectedOrg}
            vendor={selectedVendor}
            vendorName={selectedVendorName}
            onPatientSelected={props.onPatientSelected}
            org={props.org}
            foodCardOrdersOnly
            fetchOrders={props.fetchOrders}
            foodCardVendorIds={foodCardVendorIds}
            ordersMealEquivalents={ordersMealEquivalents}
            setOrdersMealEquivalents={setOrdersMealEquivalents}
            programOrdersCost={programOrdersCost}
            setProgramOrdersCost={setProgramOrdersCost}
            programBudget={props.programBudget}
            setProgramBudget={props.setProgramBudget}
          />
        )}
      </Segment>
    </div>
  );
}

export default FoodCard;
